import { DateTime } from "luxon";
import { ClassSearchContext } from "pages/newClassSearch/classSearchContext";
import {
  EField,
  fetchValues,
  IAND,
  IField,
  IFilter,
  IOR,
} from "pages/newClassSearch/classSearchQuery";
import { resetAllEventChannel } from "pages/newClassSearch/events/resetAllEventMsg";
import { ClassSearchResponse } from "pages/newClassSearch/types";
import React, { useContext, useEffect, useState } from "react";

import InterSemesterMenu from "./InterSemesterMenu";
import InterStartDateOptions from "./InterStartDateOptions";
import SemesterMenu from "./SemesterMenu";
import {
  getInterSemesterUIList,
  getSemesterUIList,
  InterSemesterUI,
} from "./shared";
import StartDateOptions from "./StartDateOptions";

export interface StarDateUI {
  startDate: string;
  endDate: string;
  isSelected: boolean;
}

export interface SemesterUI {
  semesterCode: string;
  semesterName: string;
  startDates: StarDateUI[];
  isSelected: boolean;
  fromDate: string;
  toDate: string;
  semesterStartDate: string;
  semesterEndDate: string;
}

export interface Semester {
  semesterCode: string;
  semesterName: string;
  sessionStartDate: string;
  sessionEndDate: string;
  semesterStartDate: string;
  semesterEndDate: string;
}

const StartDateField = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [semesterUIList, setSemesterUIList] = useState<SemesterUI[]>([]);
  const [interSemesterUIList, setInterSemesterUIList] = useState<
    InterSemesterUI[]
  >([]);

  const {
    state: {
      profile: { academicCareerCodeList },
      fields: { campusCode, academicCareerCode },
    },
    dispatch,
  } = useContext(ClassSearchContext);

  const fetchSemesters = async () => {
    try {
      const getFieldList = (onlineOnly: boolean) => {
        const fieldList: (IAND | IOR | IField)[] = [];

        let _campusCode = campusCode;
        if (onlineOnly) _campusCode = "ADLPH";
        fieldList.push({
          campusCode: {
            equals: _campusCode,
          },
        });

        if (
          academicCareerCode === "UGRD" &&
          academicCareerCodeList.length > 1
        ) {
          fieldList.push({
            academicCareerCodeList: {
              in: academicCareerCodeList,
            },
          } as unknown as IField);
        } else {
          fieldList.push({
            academicCareerCode: {
              equals: academicCareerCode,
            },
          });
        }

        fieldList.push({
          schedulePrint: {
            equals: "Y",
          },
        });

        fieldList.push({
          schedulePrint: {
            equals: "Y",
          },
        });

        fieldList.push({
          schedulePrint: {
            equals: "Y",
          },
        });

        fieldList.push({
          classStartDate: {
            gte: DateTime.now().toFormat("yyyy-MM-dd"),
          },
        });

        fieldList.push({
          firstEnrollmentDate: {
            lte: DateTime.now().toFormat("yyyy-MM-dd"),
          },
        });

        fieldList.push({
          lastEnrollmentDate: {
            gte: DateTime.now().toFormat("yyyy-MM-dd"),
          },
        });

        if (onlineOnly) {
          fieldList.push({
            locationCode: {
              equals: "WEB",
            },
          });
        }

        return fieldList;
      };

      let filter: IFilter;

      if (campusCode !== "ADLPH") {
        filter = {
          OR: [
            {
              AND: getFieldList(false),
            },
            {
              AND: getFieldList(true),
            },
          ],
        };
      } else {
        filter = {
          AND: getFieldList(false),
        };
      }

      const classSearchPagination = (await fetchValues(
        {
          filter: filter,
          select: [
            EField.semesterCode,
            EField.semesterName,
            EField.sessionStartDate,
            EField.sessionEndDate,
            EField.semesterStartDate,
            EField.semesterEndDate,
            EField.classStartDate,
            EField.classFormatCode,
          ],
          orderby: {
            semesterCode: "asc",
          },
        },
        "value",
        true
      )) as ClassSearchResponse;

      const semesters = classSearchPagination.value.map((item) => ({
        semesterCode: item.semesterCode,
        semesterName: item.semesterName,
        sessionStartDate: item.sessionStartDate,
        sessionEndDate: item.sessionEndDate,
        semesterStartDate: item.semesterStartDate,
        semesterEndDate: item.semesterEndDate,
      }));

      const interSemesters = classSearchPagination.value.map((item) => ({
        semesterCode: item.semesterCode,
        semesterName: item.semesterName,
        sessionStartDate: item.sessionStartDate,
        sessionEndDate: item.sessionEndDate,
        semesterStartDate: item.semesterStartDate,
        semesterEndDate: item.semesterEndDate,
        classStartDate: item.classStartDate,
        classFormatCode: item.classFormatCode,
      }));

      setSemesterUIList(getSemesterUIList(semesters));
      setInterSemesterUIList(getInterSemesterUIList(interSemesters));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (campusCode && academicCareerCode) {
      fetchSemesters();
    }
  }, [campusCode, academicCareerCode]);

  useEffect(() => {
    const unsubscribeOnResetAll = resetAllEventChannel.on("onResetAll", () => {
      setSemesterUIList((prev) =>
        prev.map((item, index) => ({
          ...item,
          isSelected: index === 0,
          startDates: item.startDates.map((startDate) => ({
            ...startDate,
            isSelected: false,
          })),
        }))
      );
      {campusCode !== "ADLPH" && (
        setInterSemesterUIList((prev) =>
          prev.map((semester) => ({
            ...semester,
            classFormats: semester.classFormats.map((classFormat) => ({
              ...classFormat,
              months: classFormat.months.map((month) => ({
                ...month,
                isSelected: false,
                startDates: month.startDates.map((startDate) => ({
                  ...startDate,
                  isSelected: false,
                })),
              })),
            })),
          }))
        )
      )}
    });

    return () => {
      unsubscribeOnResetAll();
    };
  }, []);

  return (
    <div className="inline-block min-h-[90px] rounded-md bg-du-light-gray px-4 py-2">
      <div className="mb-2 flex justify-between">
        <p className="mb-2 text-sm font-bold">Start Date</p>
        <button
          id="cs-reset-start-date"
          className="cursor-pointer text-sm font-semibold text-[#24559A]"
          onClick={() => {
            if (campusCode === "ADLPH") {
              dispatch({
                type: "UPDATE_SESSION_START_DATE_LIST",
                payload: {
                  sessionStartDateList: [],
                },
              });
              setSemesterUIList((prev) => {
                return prev.map((item) => ({
                  ...item,
                  startDates: item.startDates.map((item) => ({
                    ...item,
                    isSelected: false,
                  })),
                }));
              });
            } else {
              dispatch({
                type: "UPDATE_CLASS_START_DATE_LIST",
                payload: {
                  classStartDateList: [],
                },
              });
              setInterSemesterUIList((prev) =>
                prev.map((semester) => ({
                  ...semester,
                  classFormats: semester.classFormats.map((classFormat) => ({
                    ...classFormat,
                    months: classFormat.months.map((month) => ({
                      ...month,
                      isSelected: false,
                      startDates: month.startDates.map((startDate) => ({
                        ...startDate,
                        isSelected: false,
                      })),
                    })),
                  })),
                }))
              );
            }
          }}
        >
          Reset
        </button>
      </div>
      <div className="relative">
        <button
          id="cs-start-date-menu-button"
          className="w-full rounded bg-white px-[8px] py-[7px] text-left"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          role="button"
          tabIndex={0}
          aria-expanded={isMenuOpen ? "true" : "false"}
          aria-controls="semester-menu"
          aria-label="Start Date"
          onKeyDown={(event) => {
            if (event.key === "Escape" && isMenuOpen) {
              event.preventDefault();
              setIsMenuOpen(false);
            }
          }}
        >
          {campusCode === "ADLPH" ? (
            <StartDateOptions
              semesterUIList={semesterUIList}
              setSemesterUIList={setSemesterUIList}
            />
          ) : (
            <InterStartDateOptions
              semesterUIList={interSemesterUIList}
              setSemesterUIList={setInterSemesterUIList}
            />
          )}
        </button>
        <div
          id="semester-menu"
          className={`${!isMenuOpen ? "hidden" : ""}`}
          role="region"
          aria-live="polite"
        >
          {/* Logic here for intern semserter menu */}
          {campusCode === "ADLPH" ? (
            <SemesterMenu
              setIsMenuOpen={setIsMenuOpen}
              semesterUIList={semesterUIList}
              setSemesterUIList={setSemesterUIList}
            />
          ) : (
            <InterSemesterMenu
              setIsMenuOpen={setIsMenuOpen}
              semesterUIList={interSemesterUIList}
              setSemesterUIList={setInterSemesterUIList}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StartDateField;
